import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMediaViewUrl } from '../services/mediaService';
import { getTrips, reviewTrip } from '../services/tripService';

const PAGE_SIZE = 10;

const STATUS_DISPLAY = {
  pending: { label: 'Pending', className: 'pending' },
  pending_approval: { label: 'Pending', className: 'pending' },
  accepted: { label: 'Accepted', className: 'success' },
  published: { label: 'Accepted', className: 'success' },
  rejected: { label: 'Rejected', className: 'failed' },
  closed: { label: 'Closed', className: 'closed' },
  draft: { label: 'Draft', className: 'pending' },
  generated: { label: 'Generated', className: 'active' },
};

// GET /admin/trips filter values: all | pending | published | closed | rejected
const FILTER_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'pending', label: 'Pending' },
  { value: 'published', label: 'Accepted' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'closed', label: 'Closed' },
];

const ACTION_APPROVE = 'approve';
const ACTION_REJECT = 'reject';

const STATUS_AFTER_ACTION = {
  [ACTION_APPROVE]: 'published',
  [ACTION_REJECT]: 'rejected',
};

const eyeIcon = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const locationIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatDateRange(startDate, endDate) {
  const start = formatDate(startDate);
  const end = formatDate(endDate);
  if (start === '—' && end === '—') return '—';
  if (end === '—' || start === end) return start;
  return `${start} – ${end}`;
}

function getStatusDisplay(status) {
  const key = String(status || '').toLowerCase();
  return STATUS_DISPLAY[key] || {
    label: status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown',
    className: key || 'pending',
  };
}

function isPendingStatus(status) {
  const key = String(status || '').toLowerCase();
  return key === 'pending' || key === 'pending_approval';
}

function resolveTotalPages(pagination) {
  const pag = pagination || {};
  if (pag.totalPages != null && Number(pag.totalPages) > 0) {
    return Number(pag.totalPages);
  }
  const total = Number(pag.total ?? 0);
  if (total > 0) {
    return Math.max(1, Math.ceil(total / (Number(pag.limit) || PAGE_SIZE)));
  }
  return 1;
}

function isHttpUrl(value) {
  return /^https?:\/\//i.test(String(value || '').trim());
}

function TripImage({ src, alt }) {
  const [loaded, setLoaded] = useState(false);
  const [resolvedSrc, setResolvedSrc] = useState(() => (isHttpUrl(src) ? src : ''));
  const imgRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);

    async function resolveSrc() {
      if (!src) {
        setResolvedSrc('');
        setLoaded(true);
        return;
      }

      if (isHttpUrl(src)) {
        setResolvedSrc(src);
        return;
      }

      // heroPhoto may be an S3 key — resolve a viewable URL
      const viewUrl = await getMediaViewUrl(src);
      if (cancelled) return;
      setResolvedSrc(viewUrl || '');
      if (!viewUrl) setLoaded(true);
    }

    resolveSrc();
    return () => {
      cancelled = true;
    };
  }, [src]);

  useEffect(() => {
    const img = imgRef.current;
    // Cached images can finish before onLoad is attached — clear shimmer if already complete
    if (img?.complete && img.naturalWidth > 0) {
      setLoaded(true);
    }
  }, [resolvedSrc]);

  if (!resolvedSrc && loaded) {
    return <div className="restaurant-card-image-placeholder">No image</div>;
  }

  return (
    <>
      {!loaded && <div className="restaurant-image-shimmer" aria-hidden="true" />}
      {resolvedSrc ? (
        <img
          ref={imgRef}
          src={resolvedSrc}
          alt={alt}
          className={loaded ? 'is-loaded' : ''}
          onLoad={() => setLoaded(true)}
          onError={() => setLoaded(true)}
        />
      ) : null}
    </>
  );
}

export default function Trips() {
  const navigate = useNavigate();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [actionTarget, setActionTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const scrollRef = useRef(null);
  const sentinelRef = useRef(null);
  const prevFiltersRef = useRef({ statusFilter, debouncedSearch });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const prev = prevFiltersRef.current;
    const filtersChanged =
      prev.statusFilter !== statusFilter ||
      prev.debouncedSearch !== debouncedSearch;

    prevFiltersRef.current = { statusFilter, debouncedSearch };

    if (filtersChanged && page !== 1) {
      setPage(1);
      setTrips([]);
      setHasMore(false);
      setLoading(true);
      return undefined;
    }

    let cancelled = false;
    const append = !filtersChanged && page > 1;

    async function loadTrips() {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        if (filtersChanged) {
          setTrips([]);
          setHasMore(false);
        }
      }
      setError('');

      try {
        const response = await getTrips({
          page,
          limit: PAGE_SIZE,
          filter: statusFilter || undefined,
          search: debouncedSearch || undefined,
        });

        if (cancelled) return;

        const data = Array.isArray(response.data) ? response.data : [];
        const totalPages = resolveTotalPages(response.pagination);

        setTrips((prevList) => (append ? [...prevList, ...data] : data));
        setHasMore(page < totalPages);
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Failed to load trips.');
        if (!append) {
          setTrips([]);
          setHasMore(false);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    }

    loadTrips();
    return () => {
      cancelled = true;
    };
  }, [page, statusFilter, debouncedSearch]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const root = scrollRef.current;
    if (!sentinel || !root) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        // Guard empty lists: after a filter reset the sentinel is visible while
        // loading is still false for one frame, which used to bump page to 2.
        if (
          entries[0]?.isIntersecting &&
          hasMore &&
          !loading &&
          !loadingMore &&
          trips.length > 0
        ) {
          setPage((prev) => prev + 1);
        }
      },
      { root, rootMargin: '160px', threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, trips.length]);

  function openReviewModal(trip, action) {
    setRejectReason('');
    setActionTarget({ trip, action });
  }

  function closeReviewModal() {
    if (actionLoading) return;
    setActionTarget(null);
    setRejectReason('');
  }

  async function confirmTripAction() {
    if (!actionTarget || actionLoading) return;
    const { trip, action } = actionTarget;
    setActionLoading(true);
    try {
      const reason = action === ACTION_REJECT ? rejectReason.trim() : '';
      await reviewTrip(trip.id, {
        action,
        ...(reason ? { reason } : {}),
      });
      const nextStatus = STATUS_AFTER_ACTION[action];
      setTrips((prev) =>
        prev.map((item) => (item.id === trip.id ? { ...item, status: nextStatus } : item))
      );
      setActionTarget(null);
      setRejectReason('');
    } catch (err) {
      alert(err.message || `Failed to ${action === ACTION_APPROVE ? 'accept' : 'reject'} trip.`);
    } finally {
      setActionLoading(false);
    }
  }

  const showInitialLoading = loading && trips.length === 0;

  return (
    <div className="trips-page restaurants-page main-bg-top">
      <div className="restaurants-toolbar">
        <div className="users-search restaurants-search">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            placeholder="Search by destination or title..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="restaurants-toolbar-actions">
          <div className="filter-select-wrap">
            <select
              className="filter-select"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
                setLoading(true);
                setTrips([]);
                setHasMore(false);
              }}
            >
              {FILTER_OPTIONS.map((option) => (
                <option key={option.value || 'all'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {error && trips.length === 0 && <div className="restaurants-error">{error}</div>}

      <div className="trips-cards-scroll" ref={scrollRef}>
        {showInitialLoading ? (
          <div className="restaurants-empty">Loading trips...</div>
        ) : (
          <>
            <div className="restaurants-grid">
              {trips.map((trip) => {
                const status = getStatusDisplay(trip.status);
                const hostName = trip.host?.name || 'Unknown host';
                const applicants = trip.totalApplications ?? trip.applicationCount ?? 0;
                const cap = trip.applicationCap;
                const applicantsLabel = cap != null ? `${applicants} / ${cap}` : `${applicants}`;
                const canReview = isPendingStatus(trip.status);

                return (
                  <article key={trip.id} className="restaurant-card">
                    <div className="restaurant-card-image">
                      {trip.heroPhoto ? (
                        <TripImage src={trip.heroPhoto} alt={trip.title || 'Trip'} />
                      ) : (
                        <div className="restaurant-card-image-placeholder">No image</div>
                      )}
                      {trip.isArchived ? (
                        <span className="restaurant-offers-badge">Archived</span>
                      ) : null}
                    </div>

                    <div className="restaurant-card-body">
                      <div className="restaurant-card-title-row">
                        <h3>{trip.title || 'Untitled Trip'}</h3>
                        <div className="trip-card-status-actions">
                          <span className={`status-badge status-${status.className}`}>{status.label}</span>
                          <button
                            type="button"
                            className="trip-card-view-btn"
                            aria-label="View trip details"
                            onClick={() => navigate(`/trips/${trip.id}`)}
                          >
                            {eyeIcon}
                          </button>
                        </div>
                      </div>

                      <div className="trip-card-meta">
                        <div className="trip-card-meta-row">
                          <span className="trip-card-meta-label">Location</span>
                          <span className="trip-card-meta-value trip-card-location-value">
                            {locationIcon}
                            {trip.destination || 'No destination'}
                          </span>
                        </div>
                        <div className="trip-card-meta-row">
                          <span className="trip-card-meta-label">Dates</span>
                          <span className="trip-card-meta-value">
                            {formatDateRange(trip.startDate, trip.endDate)}
                          </span>
                        </div>
                        <div className="trip-card-meta-row">
                          <span className="trip-card-meta-label">Host</span>
                          <span className="trip-card-meta-value">{hostName}</span>
                        </div>
                        <div className="trip-card-meta-row">
                          <span className="trip-card-meta-label">Applicants</span>
                          <span className="trip-card-meta-value">{applicantsLabel}</span>
                        </div>
                        <div className="trip-card-meta-row">
                          <span className="trip-card-meta-label">Created At</span>
                          <span className="trip-card-meta-value">{formatDate(trip.createdAt)}</span>
                        </div>
                      </div>

                      {canReview ? (
                        <div className="trip-card-actions">
                          <button
                            type="button"
                            className="trip-action-btn trip-action-accept"
                            onClick={() => openReviewModal(trip, ACTION_APPROVE)}
                          >
                            Accept
                          </button>
                          <button
                            type="button"
                            className="trip-action-btn trip-action-reject"
                            onClick={() => openReviewModal(trip, ACTION_REJECT)}
                          >
                            Reject
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </article>
                );
              })}
            </div>

            {!loading && trips.length === 0 && (
              <div className="restaurants-empty">No trips found.</div>
            )}

            <div ref={sentinelRef} className="restaurants-scroll-sentinel" aria-hidden="true" />

            {loadingMore && (
              <div className="restaurants-loading-more">Loading more trips...</div>
            )}
          </>
        )}
      </div>

      {actionTarget && (
        <div className="modal-overlay" onClick={closeReviewModal}>
          <div
            className={`modal-content trip-review-modal ${actionTarget.action === ACTION_REJECT ? 'is-reject' : ''}`}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="modal-title">
              {actionTarget.action === ACTION_APPROVE ? 'Accept Trip?' : 'Reject Trip?'}
            </h3>
            <p className="modal-message">
              Are you sure you want to {actionTarget.action === ACTION_APPROVE ? 'accept' : 'reject'}{' '}
              {actionTarget.trip.title || 'this trip'}?
            </p>

            {actionTarget.action === ACTION_REJECT ? (
              <div className="trip-reject-reason">
                <label htmlFor="trip-reject-reason" className="trip-reject-reason-label">
                  Reason <span>(optional)</span>
                </label>
                <textarea
                  id="trip-reject-reason"
                  className="trip-reject-reason-input"
                  rows={4}
                  placeholder="e.g. Photos don't meet our guidelines."
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  disabled={actionLoading}
                />
              </div>
            ) : null}

            <div className="modal-actions">
              <button
                type="button"
                className="modal-btn modal-btn-cancel"
                disabled={actionLoading}
                onClick={closeReviewModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`modal-btn modal-btn-confirm ${actionTarget.action === ACTION_REJECT ? 'modal-btn-danger' : ''}`}
                disabled={actionLoading}
                onClick={confirmTripAction}
              >
                {actionLoading
                  ? 'Saving...'
                  : actionTarget.action === ACTION_APPROVE
                    ? 'Accept'
                    : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
